/**
 * Coup de pouce — Attendu 1056 « prendre 1 %, 10 % ou 50 % d'un nombre, en
 * lien avec la proportionnalité ». TROIS pages STATIQUES, une par cas,
 * partagées par les trois generator_keys ; l'élève choisit sa page par les
 * puces nommées « 50 % », « 10 % », « 1 % ». Phrases validées en texte nu par
 * le prof le 2026-09-09 (épreuve de Musk plus Eduscol), codées mot pour mot.
 *
 * L'image que l'élève a déjà : la GRILLE DE 100 de 6e (gen_634, Eduscol :
 * « 50 % pour la moitié, 10 % pour le dixième »), le même dessin sur les
 * trois pages ; c'est là que vit « en lien avec la proportionnalité » : la
 * grille entière, c'est 100 % = 300. En orange, la zone prise : la moitié
 * gauche (50 %), la première ligne (10 %), la première case (1 %), avec sa
 * valeur écrite dessus (150, 30, 3). Puis le calcul en gros avec
 * (sans flèche, retirée partout le 2026-09-09), puis UNE ligne, le geste : « Je partage 300 en 2 parts
 * égales : 150. » Pas de vérification, pas de ligne de reconnaissance (la
 * puce la porte). Rejeté à l'épreuve de Musk : la barre partagée de 1055
 * avec cent traits fins pour 1 % (une grille plus qu'un partage) et la
 * variante « dixième de dixième » (composition, 4e).
 *
 * SACRIFIÉ : 300, pour les trois pourcentages, ne sort d'aucune carte
 * (gen_1056, auto-testé par cdp1056.self.test.js). Largeur bornée à 340 px,
 * tailles de 1048.
 */

const ORANGE = '#EA580C';
const ENCRE = '#1E293B';
const GRIS = '#94A3B8';

/** La grille 10 × 10 : cases de 18 px, de (110, 44) à (290, 224). */
const X0 = 110;
const Y0 = 44;
const CASE = 18;

/**
 * La grille, les cases de la zone `prise(ligne, colonne)` en orange plein
 * (lignes blanches entre elles), les autres blanches à bord gris.
 */
const grille = (prise) => Array.from({ length: 100 }, (_, k) => {
    const l = Math.floor(k / 10);
    const c = k % 10;
    const orange = prise(l, c);
    return `<rect x="${X0 + c * CASE}" y="${Y0 + l * CASE}" width="${CASE}" height="${CASE}" fill="${orange ? ORANGE : '#FFFFFF'}" stroke="${orange ? '#FFFFFF' : GRIS}" stroke-width="${orange ? 0.8 : 0.6}"/>`;
}).join('\n  ');

/** Une page : la grille étiquetée 100 % = 300, la zone orange et sa valeur, le calcul, la ligne. */
const page = ({ pct, valeur, prise, etiquette, aria }) => `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 318" width="100%" style="max-width:340px;height:auto;display:block" role="img"
     aria-label="${aria}">
  <!-- 1. la grille de 100 : 100 % = 300 -->
  <text x="200" y="28" font-size="14" font-weight="bold" text-anchor="middle" fill="${ENCRE}">100 % = 300</text>
  ${grille(prise)}
  <rect x="${X0}" y="${Y0}" width="${10 * CASE}" height="${10 * CASE}" fill="none" stroke="${ENCRE}" stroke-width="1.4"/>

  <!-- 2. la zone prise, sa valeur -->
  ${etiquette}

  <!-- 3. le calcul -->
  <text x="200" y="264" font-size="16" font-weight="bold" text-anchor="middle" fill="${ENCRE}">${pct} % de 300 = <tspan fill="${ORANGE}">${valeur}</tspan></text>

  <!-- 4. le geste -->
  <text x="200" y="302" font-size="15" text-anchor="middle" fill="${ENCRE}">Je partage 300 en ${pct === 50 ? 2 : (pct === 10 ? 10 : 100)} parts égales : ${valeur}.</text>
</svg>`.trim();

const SVG_50 = page({
    pct: 50,
    valeur: 150,
    prise: (l, c) => c < 5,
    etiquette: `<text x="${X0 + 2.5 * CASE}" y="${Y0 + 5 * CASE + 6}" font-size="18" font-weight="bold" text-anchor="middle" fill="#FFFFFF">150</text>`,
    aria: 'Une grille de 100 cases, étiquetée 100 % égale 300. La moitié gauche de la grille est en orange, marquée 150. 50 % de 300 égale 150. Je partage 300 en 2 parts égales : 150.',
});

const SVG_10 = page({
    pct: 10,
    valeur: 30,
    prise: (l) => l === 0,
    etiquette: `<text x="${X0 + 5 * CASE}" y="${Y0 + CASE - 4}" font-size="13" font-weight="bold" text-anchor="middle" fill="#FFFFFF">30</text>`,
    aria: 'Une grille de 100 cases, étiquetée 100 % égale 300. La première ligne de 10 cases est en orange, marquée 30. 10 % de 300 égale 30. Je partage 300 en 10 parts égales : 30.',
});

const SVG_1 = page({
    pct: 1,
    valeur: 3,
    prise: (l, c) => l === 0 && c === 0,
    etiquette: `<text x="${X0 + CASE / 2}" y="${Y0 + CASE - 4}" font-size="13" font-weight="bold" text-anchor="middle" fill="#FFFFFF">3</text>`,
    aria: 'Une grille de 100 cases, étiquetée 100 % égale 300. La première case est en orange, marquée 3. 1 % de 300 égale 3. Je partage 300 en 100 parts égales : 3.',
});

const AIDE = {
    pages: [
        { titre: '50 %', svg: SVG_50 },
        { titre: '10 %', svg: SVG_10 },
        { titre: '1 %', svg: SVG_1 },
    ],
};

const CP_1056 = {
    flash_texte_trou_saisie: AIDE,
    pct_qcm_unique: AIDE,
    pct_tableau_vf: AIDE,
};

export default CP_1056;
